import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../styles/components/teams-page.scss';
import { baseURL, baseURLnoapi, CURRENT_SEASON, AVAILABLE_SEASONS } from '../Constants';
import PageHeader from '../components/PageHeader';
import { getSeason1TeamById } from '../data/season1TeamPlayersData';

// Define Player interface
interface Player {
  steamID: string;
  name: string | null;
  playerName: string;
  role: string | null;
  age: number | null;
}

// Define Team interface (unified structure)
interface Team {
  id: string;
  name: string;
  logo: string | null;
  players: Player[];
  seed?: string;
  division?: string;
  season?: number;
  currentTournamentPlacement?: number | null;
}

// Unified team interface for display
interface DisplayTeam {
  id: string;
  name: string;
  logo: string | null;
  season: number;
  division?: string;
  seed?: string;
  players: Player[];
  tournamentPlacement?: number | null;
}

const TeamsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [teams, setTeams] = useState<DisplayTeam[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Get season from URL params, default to 2
  const urlSeason = searchParams.get('season');
  const [season, setSeason] = useState<number>(urlSeason ? parseInt(urlSeason) : CURRENT_SEASON);
  
  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ctext x='50' y='50' font-size='14' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23909eba' opacity='0.5'%3ENo Logo%3C/text%3E%3C/svg%3E";

  useEffect(() => {
    console.log('useEffect triggered for season:', season);
    const fetchTeams = async () => {
      setIsLoading(true);
      setError(null);
      setTeams([]); // Clear existing teams before fetching new ones

      try {
        let teamsData: DisplayTeam[] = [];

        // Fetch teams for the selected season from current API
        // First get basic team list
        const basicResponse = await fetch(`${baseURL}/Team/basic/${season}`);

        if (!basicResponse.ok) {
          throw new Error(`Failed to fetch Season ${season} teams: ${basicResponse.status}`);
        }

        const basicTeams: { id: string; name: string; logo: string | null }[] = await basicResponse.json();

        // Check if we got an empty array (no teams for this season yet)
        if (!basicTeams || basicTeams.length === 0) {
          // Don't treat this as an error - just no teams yet
          teamsData = [];
        } else {
          // Filter out TBD teams and fetch full data for each team (includes players)
          const filteredTeams = basicTeams.filter(team => team.name.toUpperCase() !== 'TBD');

          const teamPromises = filteredTeams.map(async (basicTeam) => {
            try {
              const teamResponse = await fetch(`${baseURL}/Team/${basicTeam.id}?season=${season}`);
              if (teamResponse.ok) {
                const fullTeam: Team = await teamResponse.json();

                // For Season 1, merge hardcoded player data
                let players = fullTeam.players || [];
                if (season === 1) {
                  const season1Data = getSeason1TeamById(basicTeam.id);
                  if (season1Data) {
                    players = season1Data.players;
                  }
                }

                return {
                  id: fullTeam.id,
                  name: fullTeam.name,
                  logo: fullTeam.logo,
                  season: season,
                  division: fullTeam.division,
                  seed: fullTeam.seed,
                  players: players,
                  tournamentPlacement: fullTeam.currentTournamentPlacement
                };
              }
            } catch (error) {
              console.error(`Failed to fetch team ${basicTeam.id}:`, error);
            }

            // Fallback to basic data if full fetch fails
            // For Season 1, try to get hardcoded player data
            let fallbackPlayers: Player[] = [];
            if (season === 1) {
              const season1Data = getSeason1TeamById(basicTeam.id);
              if (season1Data) {
                fallbackPlayers = season1Data.players;
              }
            }

            return {
              id: basicTeam.id,
              name: basicTeam.name,
              logo: basicTeam.logo,
              season: season,
              players: fallbackPlayers
            };
          });

          teamsData = await Promise.all(teamPromises);
        }
        
        console.log('Setting teams state with:', teamsData.length, 'teams');
        setTeams(teamsData);
      } catch (error) {
        console.error('Error fetching teams:', error);
        setError('Failed to load teams. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTeams();
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
    
    // Set page title
    document.title = `Teams - Season ${season} | Northern Championships Series`;
  }, [season]);

  // Helper function to get team logo URL based on team's season
  const getTeamLogoUrl = (team: DisplayTeam) => {
    // All seasons now use the same logic
    let logoUrl;
    if (team.logo && team.logo.startsWith('http')) {
      logoUrl = team.logo;
    } else if (team.logo) {
      logoUrl = `${baseURLnoapi}${team.logo}`;
    } else {
      // Get logo from the team logo endpoint
      logoUrl = `${baseURL}/Team/${team.id}/logo`;
    }
    return logoUrl;
  };

  // Helper function to get team URL based on season
  const getTeamUrl = (team: DisplayTeam) => {
    // Both seasons now use internal TeamDetailPage
    // Preserve the current season in the URL
    return `/teams/${team.id}?season=${season}`;
  };

  // Helper function to handle season change
  const handleSeasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSeason = parseInt(event.target.value);
    // Only update the URL, let the URL sync effect handle the state
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('season', newSeason.toString());
    setSearchParams(newSearchParams);
  };

  // Sync season state with URL
  useEffect(() => {
    const urlSeason = searchParams.get('season');

    if (urlSeason) {
      const seasonNum = parseInt(urlSeason);
      if (seasonNum !== season && AVAILABLE_SEASONS.includes(seasonNum)) {
        console.log('Syncing season from URL:', seasonNum);
        setSeason(seasonNum);
      }
    } else {
      // If no season in URL, add current season to URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('season', season.toString());
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams]); // Remove season from dependencies to prevent loops

  // Determine if this season has completed (has placement data from API)
  const isCompletedSeason = teams.some(team => team.tournamentPlacement != null);

  // Filter and sort teams based on search term and placement
  const filteredTeams = React.useMemo(() => {
    const filtered = teams.filter(team =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort by placement for completed seasons
    if (isCompletedSeason) {
      filtered.sort((a, b) => {
        // Teams with placement come first
        if (a.tournamentPlacement && !b.tournamentPlacement) return -1;
        if (!a.tournamentPlacement && b.tournamentPlacement) return 1;

        // Both have placements - sort by placement (1st, 2nd, 3rd, etc.)
        if (a.tournamentPlacement && b.tournamentPlacement) {
          if (a.tournamentPlacement !== b.tournamentPlacement) {
            return a.tournamentPlacement - b.tournamentPlacement;
          }

          // Same placement - use seed as tiebreaker (lower seed = better placement)
          const seedA = parseInt(a.seed || '999');
          const seedB = parseInt(b.seed || '999');
          if (seedA !== seedB) {
            return seedA - seedB;
          }

          // TODO: If seeds are also the same, could use round win/loss difference
          // For now, fall back to alphabetical
        }

        // Same placement or no placement - sort by name
        return a.name.localeCompare(b.name);
      });
    }

    return filtered;
  }, [teams, searchTerm, isCompletedSeason]);

  return (
    <div className="teams-page">
      <PageHeader 
        title={`TEAMS - SEASON ${season}`}
        subtitle={`All teams participating in Season ${season} of the Northern Championships Series`}
      >
        <input
          type="text"
          placeholder="Search teams..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <div className="season-selector">
          <select value={season} onChange={handleSeasonChange}>
            {AVAILABLE_SEASONS.map(s => (
              <option key={s} value={s}>Season {s}</option>
            ))}
          </select>
        </div>
      </PageHeader>
      
      <div className="teams-content">
        <div className="container">
          {searchTerm && (
            <div className="search-results-info">
              <p>Found {filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''} matching "{searchTerm}"</p>
              {searchTerm && (
                <button 
                  className="clear-search" 
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
            
          
          {isLoading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
            </div>
          ) : error ? (
            <div className="error-container">
              <h2>Unable to Load Teams</h2>
              <p>{error}</p>
              <button
                className="btn btn-primary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          ) : teams.length === 0 ? (
            <div className="no-teams">
              <h2>Teams Coming Soon</h2>
              <p>Season {season} teams will be announced soon. Stay tuned!</p>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="no-results">
              <h2>No Teams Found</h2>
              <p>No teams match your search criteria.</p>
              {searchTerm && (
                <button 
                  className="btn btn-outline" 
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className={`teams-list ${isCompletedSeason ? 'previous-season' : ''}`} key={`list-${season}`}>
              {filteredTeams.map(team => {
                // Determine placement class for styling
                const placementClass = team.tournamentPlacement
                  ? `placement-${team.tournamentPlacement}`
                  : '';

                return (
                  <Link
                    key={`${team.id}-${season}`}
                    to={getTeamUrl(team)}
                    className={`team-row ${placementClass}`}
                  >
                    {/* Placement accent for completed seasons */}
                    {isCompletedSeason && team.tournamentPlacement && (
                      <div className={`placement-accent placement-${team.tournamentPlacement}`}>
                        <span className="placement-number">{team.tournamentPlacement}</span>
                      </div>
                    )}

                    <div className="team-logo">
                      <img
                        key={`img-${team.id}-${season}-${Date.now()}`}
                        src={getTeamLogoUrl(team)}
                        alt={`${team.name} logo`}
                        onError={(e) => {
                          // Fallback to placeholder if logo loading fails
                          (e.target as HTMLImageElement).src = teamPlaceholderImage;
                        }}
                      />
                    </div>
                    <div className="team-info">
                      <div className="team-header">
                        <h3 className="team-name">{team.name}</h3>
                      </div>
                    <div className="team-players">
                      {team.players && team.players.length > 0 ? (
                        team.players.map((player) => (
                          <span key={player.steamID} className="player-name">
                            {player.playerName}
                          </span>
                        ))
                      ) : (
                        <span className="no-players">No players listed</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamsPage;